import { createFileRoute, Outlet } from "@tanstack/react-router";
import React from "react";
import {
  LayoutDashboard,
  Briefcase,
  ClipboardList,
  Trophy,
  FileText,
  GraduationCap,
  Award,
  Shield,
  CreditCard,
  Wallet,
  Bell,
  Heart,
  User,
  HelpCircle,
} from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import type { SidebarItem } from "@/components/layout/Sidebar";
import { requireRole } from "@/lib/route-guards";

import { useStudentWorkLogsList, useStudentCourses } from "@/hooks/useStudentData";

export const Route = createFileRoute("/student")({
  beforeLoad: () => requireRole(["student"]),
  component: StudentLayout,
});

function StudentLayout() {
  const { data: workLogs } = useStudentWorkLogsList();
  const workLogsCount = workLogs?.length || 0;

  const { data: courses } = useStudentCourses();
  const hasNewCourses = courses?.some((c: any) => c.status === "available") || false;

  const sidebarItems: SidebarItem[] = React.useMemo(() => [
    {
      label: "Dashboard",
      path: "/student/dashboard",
      icon: <LayoutDashboard size={20} />,
      section: "GENERAL",
    },
    {
      label: "My Placement",
      path: "/student/placement/",
      icon: <Briefcase size={20} />,
      section: "MY PLACEMENTS",
    },
    {
      label: "Work Logs",
      path: "/student/work-logs",
      icon: <ClipboardList size={20} />,
      section: "MY PLACEMENTS",
      ...(workLogsCount > 0
        ? { badge: { label: String(workLogsCount).padStart(2, "0"), color: "gold" } }
        : {}),
    },
    {
      label: "Performance Reviews",
      path: "/student/assessments",
      icon: <Trophy size={20} />,
      section: "MY PLACEMENTS",
    },
    {
      label: "Reports & Documents",
      path: "/student/reports",
      icon: <FileText size={20} />,
      section: "MY PLACEMENTS",
    },
    {
      label: "Courses & Training",
      path: "/student/courses",
      icon: <GraduationCap size={20} />,
      section: "DEVELOPMENT",
      ...(hasNewCourses ? { badge: { label: "New", color: "blue" } } : {}),
    },
    {
      label: "My Badges",
      path: "/student/badges",
      icon: <Award size={20} />,
      section: "DEVELOPMENT",
    },
    {
      label: "My Certificates",
      path: "/student/certificates",
      icon: <Shield size={20} />,
      section: "DEVELOPMENT",
    },
    {
      label: "CV Builder",
      path: "/student/cv",
      icon: <FileText size={20} />,
      section: "DEVELOPMENT",
    },
    {
      label: "Verification Records",
      path: "/student/verification",
      icon: <Shield size={20} />,
      section: "DEVELOPMENT",
    },
    {
      label: "Payments",
      path: "/student/payments",
      icon: <CreditCard size={20} />,
      section: "ACCOUNT",
    },
    {
      label: "Wallet & Membership",
      path: "/student/wallet",
      icon: <Wallet size={20} />,
      section: "ACCOUNT",
    },
    {
      label: "Notifications",
      path: "/student/notifications",
      icon: <Bell size={20} />,
      section: "ACCOUNT",
    },
    {
      label: "Tips",
      path: "/student/tips",
      icon: <Heart size={20} />,
      section: "ACCOUNT",
    },
    {
      label: "Help & Support",
      path: "/student/support",
      icon: <HelpCircle size={20} />,
      section: "ACCOUNT",
    },
    {
      label: "My Profile",
      path: "/student/profile",
      icon: <User size={20} />,
      section: "ACCOUNT",
    },
  ], [workLogsCount, hasNewCourses]);

  return (
    <DashboardLayout title="Student Portal" sidebarItems={sidebarItems}>
      <Outlet />
    </DashboardLayout>
  );
}